import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useTranslation, Trans } from 'react-i18next';
import { Select } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '@/types';
import heroImage from '../../assets/images/vet-hero.webp';
import { Input, PasswordInput, Button , AuthHero} from '../../components';


const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: UserRole.CUSTOMER // Default role, will be updated in onboarding
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [googleError, setGoogleError] = useState('');
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);
  const { t, i18n } = useTranslation();
  const { register, loginWithGoogle, isLoading, error, isAuthenticated, clearError } = useAuth();
  const navigate = useNavigate();

  // Check if Google Client ID is available
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const isGoogleLoginEnabled = !!googleClientId && googleClientId !== 'your-google-client-id-here';

  // Wait for Google script to load
  useEffect(() => {
    if (isGoogleLoginEnabled) {
      const timer = setTimeout(() => {
        setIsGoogleScriptLoaded(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isGoogleLoginEnabled]);

  // Redirect to onboarding after successful registration
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/onboarding', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      if (clearError) {
        clearError();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field-specific error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear global auth error
    clearError();
    setGoogleError('');
  };

  // Handler for password fields (PasswordInput has different onChange signature)
  const handlePasswordChange = (field: string) => (value: string, isValid: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field-specific error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear global auth error
    clearError();
    setGoogleError('');
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = t('auth.register.validation.nameRequired');
    } else if (formData.name.trim().length < 2) {
      errors.name = t('auth.register.validation.nameLength');
    }

    if (!formData.email.trim()) {
      errors.email = t('auth.register.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('auth.register.validation.emailFormat');
    }

    if (!formData.password) {
      errors.password = t('auth.register.validation.passwordRequired');
    } else if (formData.password.length < 6) {
      errors.password = t('auth.register.validation.passwordLength');
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = t('auth.register.validation.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('auth.register.validation.confirmPasswordMismatch');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setGoogleError('');

    if (!validateForm()) {
      return;
    }

    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: formData.role
      });
      // Navigation will be handled by the useEffect above
    } catch (error) {
      // Error is handled by the auth context
      console.error('Registration failed:', error);
    }
  };

  // Handle Google registration success
  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      clearError();
      setGoogleError('');

      if (credentialResponse.credential) {
        await loginWithGoogle(credentialResponse.credential);
        // Navigation will be handled by the useEffect above
      }
    } catch (error: any) {
      console.error('Google registration failed:', error);
      setGoogleError(error.response?.data?.message || t('auth.register.googleError', { defaultValue: 'Google registration failed' }));
    }
  };

  // Handle Google registration error
  const handleGoogleError = (error: any) => {
    console.error('Google registration error:', error);

    let errorMessage = t('auth.register.googleError', { defaultValue: 'Google registration failed' });

    // Provide more specific error messages
    if (error?.error === 'popup_closed_by_user') {
      errorMessage = 'Google sign-in popup was closed before completion.';
    } else if (error?.error === 'access_denied') {
      errorMessage = 'Access denied. Please grant permission to continue.';
    } else if (error?.error?.includes('origin')) {
      errorMessage = 'Google OAuth configuration error. Please contact administrator.';
    }

    setGoogleError(errorMessage);
  };

  return (
    <div className="relative grid min-h-screen bg-base-200 text-base-content md:grid-cols-[1.15fr,1fr]">
      <AuthHero
        eyebrow={t('auth.register.heroEyebrow')}
        headline={t('auth.register.heroHeadline')}
        description={t('auth.register.heroDescription')}
        image={heroImage}
        imageAlt="VetPintar Hero"
      />

      <div className="relative flex min-h-screen flex-col justify-center px-6 py-12 sm:px-12" style={{ background: 'linear-gradient(135deg, #FAFBFC 0%, #F8F9FD 50%, #FAFBFC 100%)' }}>
        <div className="absolute right-6 top-6 sm:right-12 z-20">
          <Select
            value={i18n.language}
            onChange={(value) => i18n.changeLanguage(value)}
            className="w-32"
            options={[
              { value: 'id', label: t('language.indonesian') },
              { value: 'en', label: t('language.english') }
            ]}
          />
        </div>
        <div className="mx-auto w-full max-w-md space-y-6">
          <div className="space-y-3 text-center">
            <span className="inline-block rounded-full border border-indigo-600/30 bg-indigo-100/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600">
              {t('common.appName')}
            </span>
            <h2 className="text-3xl font-semibold text-slate-800">{t('auth.register.title')}</h2>
            <p className="text-sm text-slate-600">{t('auth.register.subtitle')}</p>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-soft">
            {/* Subtle gradient overlay matching dashboard */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-50/30 via-transparent to-cyan-50/20"></div>
            <div className="relative space-y-6">
              {(error || googleError) && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                  <p className="font-semibold">{t('auth.register.errorTitle')}</p>
                  <p className="mt-1">{error || googleError}</p>
                </div>
              )}

              <form className="space-y-5" onSubmit={handleSubmit} noValidate>
            <Input
              id="name"
              name="name"
              type="text"
              label={t('auth.register.nameLabel')}
              placeholder={t('auth.register.namePlaceholder')}
              disabled={isLoading}
              value={formData.name}
              onChange={handleChange}
              error={formErrors.name}
              fullWidth
            />

            <Input
              id="email"
              name="email"
              type="email"
              label={t('auth.register.emailLabel')}
              placeholder={t('auth.register.emailPlaceholder')}
              disabled={isLoading}
              value={formData.email}
              onChange={handleChange}
              error={formErrors.email}
              fullWidth
            />

            <Input
              id="phone"
              name="phone"
              type="tel"
              label={t('auth.register.phoneLabel')}
              placeholder={t('auth.register.phonePlaceholder')}
              disabled={isLoading}
              value={formData.phone}
              onChange={handleChange}
              fullWidth
            />

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <PasswordInput
                  id="password"
                  name="password"
                  label={t('auth.register.passwordLabel')}
                  placeholder={t('auth.register.passwordPlaceholder')}
                  disabled={isLoading}
                  value={formData.password}
                  onChange={handlePasswordChange('password')}
                  error={formErrors.password}
                  fullWidth
                  showValidation={false}
                />
                {formData.password && (
                  <div className="mt-2 flex items-center gap-3">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className={`h-full rounded-full ${
                          formData.password.length >= 8
                            ? 'bg-emerald-400'
                            : formData.password.length >= 6
                            ? 'bg-amber-400'
                            : 'bg-rose-400'
                        }`}
                        style={{ width: `${Math.min((formData.password.length / 12) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                      {formData.password.length >= 8
                        ? t('auth.register.strength.strong')
                        : formData.password.length >= 6
                        ? t('auth.register.strength.medium')
                        : t('auth.register.strength.weak')}
                    </span>
                  </div>
                )}
              </div>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                label={t('auth.register.confirmPasswordLabel')}
                placeholder={t('auth.register.confirmPasswordPlaceholder')}
                disabled={isLoading}
                value={formData.confirmPassword}
                onChange={handlePasswordChange('confirmPassword')}
                error={formErrors.confirmPassword}
                fullWidth
                showValidation={false}
              />
            </div>

            <Button
              variant="primary"
              htmlType="submit"
              size="large"
              isLoading={isLoading}
              fullWidth
              shape="round"
              className="font-semibold"
            >
              {isLoading ? t('auth.register.submitting') : t('auth.register.submit')}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs font-medium uppercase tracking-wider">
              <span className="bg-white px-4 text-slate-400">
                {t('auth.login.divider', { defaultValue: 'Or continue with' })}
              </span>
            </div>
          </div>

          <div className="flex justify-center">
            {isGoogleLoginEnabled ? (
              isGoogleScriptLoaded ? (
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => handleGoogleError({ error: 'google_register_error' })}
                  theme="outline"
                  size="large"
                  text="signup_with"
                  width="400"
                />
              ) : (
                <div className="flex h-[44px] w-[400px] items-center justify-center rounded border border-slate-200">
                  <span className="loading loading-spinner loading-sm"></span>
                  <span className="ml-2 text-sm text-slate-600">Loading Google Sign-In...</span>
                </div>
              )
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 shadow-sm">
                <p className="font-semibold">Google Registration Not Available</p>
                <p className="mt-1 text-amber-700">Google OAuth needs to be configured. Please check GOOGLE_OAUTH_SETUP.md for instructions.</p>
              </div>
            )}
          </div>

          <p className="text-center text-sm text-slate-600">
            {t('auth.register.cta.prompt')}{' '}
            <Link to="/login" className="font-semibold text-indigo-600 transition-colors hover:text-indigo-700">
              {t('auth.register.cta.action')}
            </Link>
          </p>
        </div>
      </div>

        <p className="text-center text-xs text-slate-500">
          <Trans
            i18nKey="auth.register.terms"
            components={[
              <a key="terms" className="font-semibold text-indigo-600 hover:text-indigo-700" href="/terms">{' '}</a>,
              <a key="privacy" className="font-semibold text-indigo-600 hover:text-indigo-700" href="/privacy">{' '}</a>
            ]}
          />
        </p>
      </div>
    </div>
    </div>
  );
};

export default Register;
